// Scales a launcher widget to its grid cell. The widget's content lives in a fixed "design" space (the default grid's px) and is
// transformed to the real cell, so widgets written in px keep their proportions on any grid density. If the content still overflows
// its box (long text, a small size), the box is shrunk further until it fits. Chromium 85 safe (transform only, no `zoom`).

/** Never shrink below this share of the design scale. */
const MIN_FIT = 0.4;
const STEP = 0.9;

/** Lay the element out in design space: a box of (W, H) real px divided by the factor, scaled back by the factor. */
function layout(el: HTMLElement, W: number, H: number, f: number) {
  el.style.width = `${W / f}px`;
  el.style.height = `${H / f}px`;
  el.style.transform = f === 1 ? '' : `scale(${f})`;
}

const hasText = (e: Element) => Array.from(e.childNodes).some((n) => n.nodeType === 3 && !!n.textContent?.trim());
const isMedia = (e: Element) => /^(IMG|SVG|CANVAS|VIDEO)$/i.test(e.tagName);

interface Box { l: number; t: number; r: number; b: number }
/** Where an element is visible: `box` is the region its ancestors leave visible (root box ∩ clipping ancestors), `content` the same minus their padding (text should stay inside it), `inner` says a clipping ancestor other than the root is involved. */
interface Clip { box: Box; content: Box; inner: boolean }
/** `abs` is the clip for absolutely positioned descendants (only clipped by ancestors up to their containing block); `freeX/freeY`: this subtree is an item of a scrolling row/column, partial visibility along that axis is intended. */
interface Ctx { clip: Clip; abs: Clip; freeX: boolean; freeY: boolean }

const rectBox = (r: DOMRect): Box => ({ l: r.left, t: r.top, r: r.right, b: r.bottom });
const inter = (a: Box, b: Box): Box => ({ l: Math.max(a.l, b.l), t: Math.max(a.t, b.t), r: Math.min(a.r, b.r), b: Math.min(a.b, b.b) });
const px = (v: string) => parseFloat(v) || 0;

/** Union of the geometry of the element's own text nodes (screen px). Line boxes are trimmed to the line-height so tight line-heights do not read as cut. */
function textBox(c: Element, cs: CSSStyleDeclaration, f: number): Box | undefined {
  let box: Box | undefined;
  const range = document.createRange();
  const lh = px(cs.lineHeight) * f;
  for (const n of Array.from(c.childNodes)) {
    if (n.nodeType !== 3 || !n.textContent?.trim()) continue;
    range.selectNodeContents(n);
    for (const q of Array.from(range.getClientRects())) {
      if (q.width <= 0 && q.height <= 0) continue;
      const inset = lh > 0 && q.height > lh ? (q.height - lh) / 2 : 0;
      const b = { l: q.left, t: q.top + inset, r: q.right, b: q.bottom - inset };
      box = box ? { l: Math.min(box.l, b.l), t: Math.min(box.t, b.t), r: Math.max(box.r, b.r), b: Math.max(box.b, b.b) } : b;
    }
  }
  return box;
}

/**
 * How much content is cut off or sticks out? 0 = everything fits, otherwise roughly "needed size / available size" (> 1) of the worst offender.
 * Counts: anything sticking out of the root's right/bottom edge (when not clipped); text and images that a clipping ancestor cuts, on any side
 * (a centered flex item wider than its card is cut on both); text that overflows its own box or is clipped by it without being meant to be
 * truncated. Intentional overflow is allowed: `text-overflow: ellipsis`, `-webkit-line-clamp`, scroll containers (`overflow: auto|scroll`, or
 * anything already scrolled), the SDK shelf, Tile text and FitText (they fit themselves, asynchronously), the tile caption, `data-fl-nofit`, fixed layers, hidden/animated subtrees, absolutely positioned
 * images (backgrounds), items of a scrolling row (all children `flex-shrink: 0` in a clipping flex container) along the row's axis.
 */
function overflow(root: HTMLElement, f: number): number {
  const rb = rectBox(root.getBoundingClientRect());
  const tol = 1.5 * f;
  let worst = 0;
  const flag = (b: Box, bounds: Box) => {
    const rx = (Math.max(b.r, bounds.r) - Math.min(b.l, bounds.l)) / Math.max(1, bounds.r - bounds.l);
    const ry = (Math.max(b.b, bounds.b) - Math.min(b.t, bounds.t)) / Math.max(1, bounds.b - bounds.t);
    worst = Math.max(worst, Math.max(1.03, rx, ry));
  };

  const visit = (parent: Element, cx: Ctx, ps: CSSStyleDeclaration | undefined) => {
    const kids: [Element, CSSStyleDeclaration][] = [];
    for (const c of Array.from(parent.children)) {
      if (c.hasAttribute('data-fl-nofit') || c.classList.contains('fl-tile-caption') || c.classList.contains('fl-shelf-track') || c.classList.contains('fl-tile-text') || c.classList.contains('fl-fit') || /^(SCRIPT|STYLE)$/i.test(c.tagName)) continue;
      const cs = getComputedStyle(c);
      if (cs.display === 'none' || cs.position === 'fixed' || cs.visibility === 'hidden' || cs.opacity === '0' || cs.animationName !== 'none') continue;
      kids.push([c, cs]);
    }
    // A clipping flex container whose items never shrink is a scrolling row/column: its items may be partly out of view along the main axis.
    let carouselX = false;
    let carouselY = false;
    if (ps && kids.length >= 2 && ps.display.includes('flex') && ps.flexWrap === 'nowrap' && (ps.overflowX !== 'visible' || ps.overflowY !== 'visible')) {
      if (kids.every(([, cs]) => cs.position === 'absolute' || cs.flexShrink === '0')) {
        if (ps.flexDirection.startsWith('row')) carouselX = true;
        else carouselY = true;
      }
    }
    for (const [c, cs] of kids) {
      const abs = cs.position === 'absolute';
      const clip = abs ? cx.abs : cx.clip;
      const cb = clip.content;
      const freeX = cx.freeX || carouselX;
      const freeY = cx.freeY || carouselY;
      const r = c.getBoundingClientRect();
      const visible = r.width > 0 || r.height > 0;
      const media = isMedia(c);
      if (visible) {
        const rbx = rectBox(r);
        const b = clip.box;
        // Wholly outside the visible region of a clipping ancestor: hidden on purpose (off-screen panel, next slide...).
        if (clip.inner && (rbx.r <= b.l || rbx.l >= b.r || rbx.b <= b.t || rbx.t >= b.b)) continue;
        const text = !media && cs.display !== 'contents' && hasText(c);
        if (!clip.inner && !text && !media) {
          // A plain box sticking out of the root on the right/bottom (visible, so it would overlap the neighbours).
          if (rbx.r > b.r + tol || rbx.b > b.b + tol) flag(rbx, b);
        } else if (media) {
          if (!abs) {
            const cutX = !freeX && (rbx.l < b.l - tol || rbx.r > b.r + tol);
            const cutY = !freeY && (rbx.t < b.t - tol || rbx.b > b.b + tol);
            if (cutX || cutY) flag(rbx, b);
          }
        } else if (text) {
          const clipsX = cs.overflowX !== 'visible';
          const clipsY = cs.overflowY !== 'visible';
          const boxCut = rbx.l < cb.l - tol || rbx.r > cb.r + tol || rbx.t < cb.t - tol || rbx.b > cb.b + tol;
          const clamp = cs.getPropertyValue('-webkit-line-clamp') !== 'none';
          const inline = cs.display === 'inline';
          const cw = c.clientWidth;
          const spillX = !inline && cw > 0 && c.scrollWidth > cw + 2;
          // A tight line-height makes the glyph box (scrollHeight) taller than the line box by up to ~0.3 em: that is not overflow.
          const spillY = !inline && c.clientHeight > 0 && c.scrollHeight > c.clientHeight + Math.max(2, 0.3 * (parseFloat(cs.fontSize) || 16));
          if (boxCut || spillX || spillY) {
            // The text itself, as far as it is visible: clipped to its own box when the element clips.
            const tx = textBox(c, cs, f);
            if (tx) {
              let vis = tx;
              if (clipsX) vis = { ...vis, l: Math.max(vis.l, rbx.l), r: Math.min(vis.r, rbx.r) };
              if (clipsY) vis = { ...vis, t: Math.max(vis.t, rbx.t), b: Math.min(vis.b, rbx.b) };
              // Text spilling out of its own unclipped box: its real extent counts (line boxes would already show it, the scroll size covers wrapped overflow).
              let ext = vis;
              if (!clipsX && spillX) ext = { ...ext, r: Math.max(ext.r, rbx.l + c.scrollWidth * f) };
              if (!clipsY && spillY) ext = { ...ext, b: Math.max(ext.b, rbx.t + c.scrollHeight * f) };
              const cutX = !freeX && (ext.l < cb.l - tol || ext.r > cb.r + tol);
              const cutY = !freeY && (ext.t < cb.t - tol || ext.b > cb.b + tol);
              if (cutX || cutY) flag(ext, cb);
              // Text clipped by its own box, unless that is the point (ellipsis / line clamp).
              const truncX = clipsX && !freeX && spillX && !clamp && cs.textOverflow !== 'ellipsis';
              const truncY = clipsY && !freeY && spillY && !clamp;
              if (truncX || truncY) {
                const need = Math.max(truncX ? c.scrollWidth / cw : 1, truncY ? c.scrollHeight / c.clientHeight : 1);
                worst = Math.max(worst, Math.max(1.03, need));
              }
            }
          }
        }
      }
      if (media) continue;
      // Scroll containers (or anything already scrolled) show their overflow by scrolling: don't look inside.
      if (cs.overflowX === 'auto' || cs.overflowX === 'scroll' || cs.overflowY === 'auto' || cs.overflowY === 'scroll') continue;
      const clips = cs.overflowX !== 'visible' || cs.overflowY !== 'visible';
      if (clips && (c.scrollLeft !== 0 || c.scrollTop !== 0)) continue;
      let self = clip;
      if (clips) {
        const pad = (side: 'Top' | 'Right' | 'Bottom' | 'Left') => (px(cs.getPropertyValue(`padding-${side.toLowerCase()}`)) + px(cs.getPropertyValue(`border-${side.toLowerCase()}-width`))) * f;
        const own = { l: r.left + pad('Left'), t: r.top + pad('Top'), r: r.right - pad('Right'), b: r.bottom - pad('Bottom') };
        self = { box: inter(clip.box, rectBox(r)), content: inter(clip.content, own), inner: true };
      }
      const positioned = abs || cs.position !== 'static' || cs.transform !== 'none';
      visit(c, { clip: self, abs: positioned ? self : cx.abs, freeX, freeY }, cs);
    }
  };
  const rc: Clip = { box: rb, content: rb, inner: false };
  visit(root, { clip: rc, abs: rc, freeX: false, freeY: false }, getComputedStyle(root));
  return worst;
}

/** Size the widget element for a real box (W × H px) at design scale `k`, shrinking further until its content fits. Returns the final factor. */
export function fitWidget(el: HTMLElement, W: number, H: number, k: number): number {
  el.setAttribute('data-fl-measure', '');
  let s = 1;
  try {
    layout(el, W, H, k);
    const r0 = overflow(el, k);
    if (!r0) return k;
    // Jump by the measured overflow ratio (at least one STEP); the widget's box grows in layout px as the factor drops.
    let hi = 1; // largest factor known to overflow
    let lo = 0; // largest factor known to fit
    let r = r0;
    for (let i = 0; i < 8; i++) {
      const next = Math.max(MIN_FIT, Math.min(hi * STEP, hi / r));
      layout(el, W, H, k * next);
      r = overflow(el, k * next);
      if (!r) { lo = next; break; }
      hi = next;
      if (next <= MIN_FIT) break;
    }
    if (lo) {
      // Refine between the last overflowing and the first fitting factor: the jump may have overshot.
      for (let i = 0; i < 4 && hi - lo > 0.02; i++) {
        const mid = (lo + hi) / 2;
        layout(el, W, H, k * mid);
        if (overflow(el, k * mid)) hi = mid;
        else lo = mid;
      }
      s = lo;
    } else {
      // Never fit. If shrinking hardly helped (fixed-size inner boxes) keep the design scale, else the smallest factor.
      s = r < r0 * 0.9 ? MIN_FIT : 1;
    }
    layout(el, W, H, k * s);
  } finally {
    el.removeAttribute('data-fl-measure');
  }
  return k * s;
}

/**
 * Keep a widget element fitted: now, and again whenever its content changes (text, children, images loading).
 * `get()` returns the current real box and scale (the grid can change between refits). Returns a cleanup function.
 */
export function watchWidget(el: HTMLElement, get: () => { W: number; H: number; k: number }): () => void {
  let queued = false;
  const run = () => {
    queued = false;
    if (!el.isConnected) return;
    const { W, H, k } = get();
    fitWidget(el, W, H, k);
  };
  const schedule = () => {
    if (queued) return;
    queued = true;
    queueMicrotask(run);
  };
  run();
  // Our own writes are attribute changes on the root, which are not observed, so this cannot loop.
  const mo = new MutationObserver(schedule);
  mo.observe(el, { childList: true, characterData: true, subtree: true });
  el.addEventListener('load', schedule, true);
  return () => {
    mo.disconnect();
    el.removeEventListener('load', schedule, true);
  };
}
